import React from 'react';

import lazyImport from '../utils/lazyImport';

import AtolReports from './AtolReports';
import AtolReportsV2 from './AtolReportsV2';
import CharterFlightsPricingPage from './Charter/CharterFlightsPricingPage';
import CharterInventoryPage from './Charter/CharterInventoryPage';
import CharterLoyaltyPointsPage from './Charter/CharterLoyaltyPointsPage';
import CharterSeatsPage from './Charter/CharterSeatsPage';
import CharterFriendAndFamilyPage from './Charter/FriendAndFamily/CharterFriendAndFamilyPage';
import CharterUpgradePricingPage from './Charter/UpgradePricing/CharterUpgradePricingPage';
import CreditReservationList from './CreditReservationListContainer';
import FDAPage from './FDA/FDAPage';
import FlightCacheToolPage from './FlightCacheTool';
import MerchandisingFlightDeals from './FlightDeals/FlightDeals';
import { FlightForceBundles } from './FlightForceBundles';
import { FlightsCampaings } from './FlightsCampaigns/FlightsCampaigns';
import JetstarBspImportMonitoringPage from './JetstarBsp/JetstarBspImportMonitoringPage';
import PendingReservations from './PendingReservations';

interface FlightsBaseTab {
  slug: string;
  label: string;
}

export interface FlightsLeafTab extends FlightsBaseTab {
  type: 'leaf';
  component: React.ElementType;
}

export interface FlightsGroupedTab extends FlightsBaseTab {
  type: 'group';
  defaultChildSlug: string;
  children: ReadonlyArray<FlightsLeafTab>;
}

export type FlightsPrimaryTab = FlightsLeafTab | FlightsGroupedTab;

export const DEFAULT_FLIGHTS_LEAF_TAB_SLUG = 'campaigns';

function isGroupedFlightsTab(tab: FlightsPrimaryTab): tab is FlightsGroupedTab {
  return tab.type === 'group';
}

export const flightsPrimaryTabs: ReadonlyArray<FlightsPrimaryTab> = [
  {
    type: 'leaf',
    slug: 'campaigns',
    label: 'Campaigns',
    component: FlightsCampaings,
  },
  {
    type: 'leaf',
    slug: 'deals',
    label: 'Deals',
    component: MerchandisingFlightDeals,
  },
  {
    type: 'leaf',
    slug: 'dashboard',
    label: 'Dashboard',
    component: lazyImport(() => import('./FlightDashboard')),
  },
  {
    type: 'leaf',
    slug: 'pending',
    label: 'Pending',
    component: PendingReservations,
  },
  {
    type: 'leaf',
    slug: 'credit',
    label: 'Credit Reservation',
    component: CreditReservationList,
  },
  {
    type: 'leaf',
    slug: 'atol-reports',
    label: 'ATOL Reports by Month (Older Feature)',
    component: AtolReports,
  },
  {
    type: 'leaf',
    slug: 'atol-reports-new',
    label: 'ATOL Reports by Month (New)',
    component: AtolReportsV2,
  },
  {
    type: 'leaf',
    slug: 'force-bundles',
    label: 'Force Bundles',
    component: FlightForceBundles,
  },
  {
    type: 'leaf',
    slug: 'fda',
    label: 'Flights Configuration',
    component: FDAPage,
  },
  {
    type: 'leaf',
    slug: 'cacheTool',
    label: 'Caching Tool',
    component: FlightCacheToolPage,
  },
  {
    type: 'leaf',
    slug: 'jetstarBspImports',
    label: 'Jetstar BSP Imports',
    component: JetstarBspImportMonitoringPage,
  },
  {
    type: 'group',
    slug: 'charterConfiguration',
    label: 'Charter Configuration',
    defaultChildSlug: 'charterSeats',
    children: [
      {
        type: 'leaf',
        slug: 'charterSeats',
        label: 'Charter Seats',
        component: CharterSeatsPage,
      },
      {
        type: 'leaf',
        slug: 'charterInventory',
        label: 'Charter Inventory',
        component: CharterInventoryPage,
      },
      {
        type: 'leaf',
        slug: 'charterLoyaltyPoints',
        label: 'Charter Loyalty Points',
        component: CharterLoyaltyPointsPage,
      },
      {
        type: 'leaf',
        slug: 'charterPrices',
        label: 'Charter Prices',
        component: CharterFlightsPricingPage,
      },
      {
        type: 'leaf',
        slug: 'charterUpgrades',
        label: 'Charter Upgrade Pricing',
        component: CharterUpgradePricingPage,
      },
      {
        type: 'leaf',
        slug: 'charterFriendAndFamily',
        label: 'Charter Friends and Family',
        component: CharterFriendAndFamilyPage,
      },
    ],
  },
];

const flightsPrimaryTabsBySlug = flightsPrimaryTabs.reduce<Record<string, FlightsPrimaryTab>>(
  (result, tab) => ({
    ...result,
    [tab.slug]: tab,
  }),
  {},
);

const flattenedLeafTabsWithParents = flightsPrimaryTabs.flatMap((tab) =>
  isGroupedFlightsTab(tab)
    ? tab.children.map((child) => ({
        leafTab: child,
        parentSlug: tab.slug,
      }))
    : [
        {
          leafTab: tab,
          parentSlug: tab.slug,
        },
      ],
);

export const flightsLeafTabs = flattenedLeafTabsWithParents.map(({ leafTab }) => leafTab);

const flightsLeafTabsBySlug = flightsLeafTabs.reduce<Record<string, FlightsLeafTab>>(
  (result, tab) => ({
    ...result,
    [tab.slug]: tab,
  }),
  {},
);

const flightsLeafTabParentSlugByLeafSlug = flattenedLeafTabsWithParents.reduce<Record<string, string>>(
  (result, tab) => ({
    ...result,
    [tab.leafTab.slug]: tab.parentSlug,
  }),
  {},
);

function getSafeLeafSlug(selectedLeafSlug: string | null): string {
  if (selectedLeafSlug && flightsLeafTabsBySlug[selectedLeafSlug]) {
    return selectedLeafSlug;
  }

  if (flightsLeafTabsBySlug[DEFAULT_FLIGHTS_LEAF_TAB_SLUG]) {
    return DEFAULT_FLIGHTS_LEAF_TAB_SLUG;
  }

  return flightsLeafTabs[0]?.slug ?? '';
}

export interface FlightsTabSelection {
  activeLeafTab: FlightsLeafTab;
  activePrimaryTab: FlightsPrimaryTab;
  activeLeafSlug: string;
  activePrimarySlug: string;
}

export function resolveFlightsTabSelection(selectedLeafSlug: string | null): FlightsTabSelection {
  const activeLeafSlug = getSafeLeafSlug(selectedLeafSlug);
  const activeLeafTab = flightsLeafTabsBySlug[activeLeafSlug];
  const activePrimarySlug = flightsLeafTabParentSlugByLeafSlug[activeLeafSlug];
  const activePrimaryTab = flightsPrimaryTabsBySlug[activePrimarySlug];

  return {
    activeLeafSlug,
    activePrimarySlug,
    activeLeafTab,
    activePrimaryTab,
  };
}

export function getFlightsSecondaryTabs(primaryTab: FlightsPrimaryTab): ReadonlyArray<FlightsLeafTab> {
  return isGroupedFlightsTab(primaryTab) ? primaryTab.children : [];
}

export function getFlightsLeafSlugForPrimaryTab(primaryTabSlug: string): string | null {
  const selectedPrimaryTab = flightsPrimaryTabsBySlug[primaryTabSlug];

  if (!selectedPrimaryTab) {
    return null;
  }

  return isGroupedFlightsTab(selectedPrimaryTab) ? selectedPrimaryTab.defaultChildSlug : selectedPrimaryTab.slug;
}
